import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TmtLoggerService } from 'tmt-logger';

import { environment } from 'src/environments/environment';
import { UserData } from 'src/models/user-data.model';
/**
 * Service handling tasks related to user authorization.
 */
@Injectable({
	providedIn: 'root',
})
export class AuthService {
	/**
	 *
	 * @param router
	 * @param route
	 * @param loggerService
	 */
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private loggerService: TmtLoggerService,
	) {}

	private userDataSubject: BehaviorSubject<UserData> = new BehaviorSubject({
		sessionID: localStorage.getItem('sessionId'),
		userName: localStorage.getItem('userName'),
		isLoggedIn: !!localStorage.getItem('sessionId'),
		companyName: localStorage.getItem('companyName'),
		firstName: localStorage.getItem('firstName'),
		lastName: localStorage.getItem('lastName'),
		functionalGroups: localStorage.getItem('functionalGroups')?.split(';'),
	} as UserData);

	public userData$ = this.userDataSubject.asObservable();

	public isLoggedIn$ = this.userData$.pipe(map(userData => !!userData.sessionID || !!localStorage.getItem('sessionId')));

	public startedRedirect = false;

	public sessionID$ = this.userData$.pipe(map(userData => userData.sessionID ?? localStorage.getItem('sessionId')));

	/**
	 * Clears all session data.
	 */
	public clearSession(): void {
		if (!this.userDataSubject.value.sessionID) {
			return;
		}

		this.userDataSubject.next({ userName: '', sessionID: '', isLoggedIn: false, companyName: '', firstName: '', lastName: '', functionalGroups: [] });

		// Clear data from local storage
		localStorage.removeItem('sessionId');
		localStorage.removeItem('userName');
		localStorage.removeItem('companyName');
		localStorage.removeItem('firstName');
		localStorage.removeItem('lastName');
		localStorage.removeItem('functionalGroups');
	}

	/**
	 * Sets new user session data.
	 * @param userData New user data.
	 */
	public updateSession(userData: UserData) {
		localStorage.setItem('sessionId', userData.sessionID);
		localStorage.setItem('userName', userData.userName);
		localStorage.setItem('companyName', userData.companyName);
		localStorage.setItem('firstName', userData.firstName);
		localStorage.setItem('lastName', userData.lastName);
		localStorage.setItem('functionalGroups', userData.functionalGroups.join(';'));

		userData.isLoggedIn = !!userData.sessionID;

		const newUserData = {
			userName: userData.userName,
			sessionID: userData.sessionID,
			isLoggedIn: !!userData.sessionID,
			companyName: userData.companyName,
			email: userData.email,
			firstName: userData.firstName,
			lastName: userData.lastName,
			functionalGroups: userData.functionalGroups,
		};
		this.userDataSubject.next(newUserData);
	}

	/**
	 * Performs login.
	 */
	public login() {
		const host = encodeURIComponent(window.location.href);
		const loginUrl = `${environment.redirectUrl}?origin=${host}`;
		this.loggerService.flush();
		document.location.href = loginUrl;
	}

	/**
	 * Removes input query params from URL, to handle removing sessionID etc.
	 * @param paramsToRemove
	 */
	public removeSpecificQueryParams(paramsToRemove: string[]) {
		// Get the current query parameters
		const queryParams = { ...this.route.snapshot.queryParams };

		// Remove specific parameters
		paramsToRemove.forEach(param => delete queryParams[param]);

		// Construct a new URL without the removed parameters
		const urlTree = this.router.createUrlTree([], { queryParams });

		// Navigate to the new URL
		this.router.navigateByUrl(urlTree);
	}
}
